/*
 * Input hygiene for every player-app form.
 *
 * It is defence in depth only. It never replaces server-side validation,
 * parameterised queries or a safely configured XML parser.
 */

export type InputKind = "text" | "email" | "password" | "number" | "textarea";

export type InputIssue =
  | "required"
  | "control"
  | "markup"
  | "tooLong"
  | "tooShort"
  | "email"
  | "number";

export interface InputRules {
  kind?: InputKind;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
}

/** Issues worth showing before submit: they are never fixed by typing on. */
export const EARLY_ISSUES: InputIssue[] = ["control", "markup", "tooLong"];

export const toText = (value: unknown): string =>
  typeof value === "string" ? value : value == null ? "" : String(value);

/** The value as submitted: NFKC-normalised and trimmed. Passwords are never altered. */
export const finalizeInput = (value: unknown, kind: InputKind = "text"): string => {
  const text = toText(value);
  return kind === "password" ? text : text.normalize("NFKC").trim();
};

const BIDI_CONTROLS = /[‪-‮⁦-⁩]/;

/** C0 and C1 controls, DEL and bidi overrides. A textarea keeps tab and line breaks. */
const hasControl = (text: string, multiline: boolean) => {
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (multiline && (code === 9 || code === 10 || code === 13)) continue;
    if (code < 32 || (code >= 127 && code <= 159)) return true;
  }
  return BIDI_CONTROLS.test(text);
};

/*
 * Markup that has no business in a plain-text field. Deliberately narrow:
 * a lone <, >, & or quote is ordinary text ("a < b", "Tom & Jerry").
 */
const MARKUP = [
  /<!DOCTYPE/i,
  /<!ENTITY/i,
  /<!\[CDATA\[/i,
  /<\?xml/i,
  /\b(?:SYSTEM|PUBLIC)\s+["']/, // a DTD external identifier
  /<\/?[a-z][\w:.-]*(?:\s[^<>]*)?\/?>/i, // a complete tag: <b>, </div>, <x:y a="1"/>
  /<(?:script|iframe|object|embed|svg|math|style|link|meta|base|form)\b/i,
  /&#(?:x[0-9a-f]+|\d+);/i, // numeric character reference
];

const EMAIL = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]+$/;
const NUMBER = /^\d+(?:\.\d+)?$/;

/** The first problem with a value, or null. Never throws, whatever it is given. */
export const inputIssue = (
  value: unknown,
  { kind = "text", required, minLength, maxLength }: InputRules = {}
): InputIssue | null => {
  const text = finalizeInput(value, kind);
  if (!text) return required ? "required" : null;
  if (hasControl(text, kind === "textarea")) return "control";
  if (kind !== "password" && MARKUP.some((pattern) => pattern.test(text))) return "markup";
  if (maxLength !== undefined && text.length > maxLength) return "tooLong";
  if (minLength !== undefined && text.length < minLength) return "tooShort";
  if (kind === "email" && !EMAIL.test(text)) return "email";
  if (kind === "number" && !NUMBER.test(text)) return "number";
  return null;
};
